// Client-side Bicep generation — replaces Flask backend for static deployment
// Ported from backend/azure_naming.py + backend/app.py

const PURPOSE_CODES = {
  kv_infrastructure: '1',
  kv_application: '2',
  kv_data: '3',
  sa_boot_diagnostics: '11',
  sa_application_data: '12',
  sa_logs: '13'
};

const REGION_CODES = {
  westus2: 'az1',
  eastus: 'az2',
  centralus: 'az3'
};

const VM_COSTS = {
  Standard_B2s: 30,
  Standard_B2ms: 60,
  Standard_D2s_v3: 70,
  Standard_D4s_v3: 140
};

export function extractIdentity(subscriptionName) {
  const cleaned = subscriptionName.replace(/[^a-zA-Z0-9]/g, '');
  const front9 = cleaned.length >= 9 ? cleaned.slice(0, 9) : cleaned;
  const back9 = cleaned.length >= 9 ? cleaned.slice(-9) : cleaned;
  return { front9, back9 };
}

export function generateKeyvaultName(front9, back9, purpose) {
  const code = PURPOSE_CODES[purpose] || '1';
  const name = `kv${code}${front9}-${back9}`;
  const errors = [];
  if (name.length > 24) errors.push(`Name exceeds 24 character limit (current: ${name.length})`);
  if (name.length < 3) errors.push(`Name must be at least 3 characters (current: ${name.length})`);
  if (!/^[a-zA-Z0-9-]+$/.test(name)) errors.push('Name contains invalid characters (only alphanumeric and dash allowed)');
  if (name.length > 0 && !/^[a-zA-Z]/.test(name)) errors.push('Name must start with a letter');
  return { isValid: errors.length === 0, name, errors, length: name.length, maxLength: 24 };
}

export function generateStorageAccountName(front9, back9, purpose) {
  const code = PURPOSE_CODES[purpose] || '11';
  const name = `sa${code}${front9}${back9}`.toLowerCase();
  const errors = [];
  if (name.length > 24) errors.push(`Name exceeds 24 character limit (current: ${name.length})`);
  if (name.length < 3) errors.push(`Name must be at least 3 characters (current: ${name.length})`);
  if (!/^[a-z0-9]+$/.test(name)) errors.push('Name must contain only lowercase letters and numbers');
  return { isValid: errors.length === 0, name, errors, length: name.length, maxLength: 24 };
}

function calculateCosts(vmSize, useStorage, useLaw) {
  const vmCost = VM_COSTS[vmSize] || 30;
  const diskCost = 5;
  const kvCost = 0.03;
  const storageCost = useStorage ? 3 : 0;
  const lawCost = useLaw ? 20 : 0;
  const total = vmCost + diskCost + kvCost + storageCost + lawCost;
  return {
    vm: vmCost,
    disk: diskCost,
    keyvault: kvCost,
    storage: storageCost,
    logAnalytics: lawCost,
    total: Math.round(total * 100) / 100
  };
}

function buildTagsBicep(tagsDict) {
  return Object.entries(tagsDict)
    .map(([k, v]) => `${k}: '${v}'`)
    .join('\n    ');
}

function getRegionCode(region) {
  return REGION_CODES[region] || 'az2';
}

/**
 * Replaces POST /api/preview — generates resource names, validation, and costs.
 * @param {object} state - Full wizard state from localStorage
 * @returns {{ resources, costs, options, allValid }}
 */
export function generatePreview(state) {
  const page2 = state.page2Discovery || {};
  const page3 = state.page3VMConfig || {};
  const page5 = state.page5StorageAccount || {};
  const page6 = state.page6LogAnalytics || {};

  const subscriptionName = page2.subscription || 'Default-Subscription';
  const { front9, back9 } = extractIdentity(subscriptionName);

  // VM config
  const environment = page3.environment || 'production';
  const region = page3.region || 'eastus';
  const vmSize = page3.vmSize || 'Standard_B2s';
  const vmFunction = page3.vmFunction || '';
  const vmPurpose = page3.vmPurpose || '';
  const instance = page3.instance || '01';
  const adminUser = page3.adminUser || 'vmadmin';
  let finalVMName = page3.finalVMName || '';

  if (!finalVMName) {
    const envCode = environment.slice(0, 3);
    const regionCode = getRegionCode(region);
    finalVMName = `${envCode}${regionCode}${vmFunction}${vmPurpose}${instance}`;
  }

  const vmValid = finalVMName.length > 0 && finalVMName.length <= 15;

  // KeyVault (always required)
  const kvResult = generateKeyvaultName(front9, back9, 'kv_infrastructure');

  // Optional resources
  const useStorage = page5.useStorageAccount || false;
  const useLaw = page6.useLogAnalytics || false;

  const saResult = useStorage ? generateStorageAccountName(front9, back9, 'sa_boot_diagnostics') : null;

  const cleanedSub = subscriptionName.replace(/[^a-zA-Z0-9]/g, '');
  const lawName = useLaw ? `law-${cleanedSub}` : null;

  // Dependent resources
  const nicName = `nic-${finalVMName}`;
  const diskName = `disk-${finalVMName}-os`;

  // Costs
  const costs = calculateCosts(vmSize, useStorage, useLaw);

  // Build resources response
  const resources = {
    keyvault: {
      name: kvResult.name,
      valid: kvResult.isValid,
      length: kvResult.length,
      maxLength: kvResult.maxLength,
      errors: kvResult.errors,
      purpose: 'Infrastructure secrets',
      required: true
    },
    vm: {
      name: finalVMName,
      valid: vmValid,
      length: finalVMName.length,
      maxLength: 15,
      size: vmSize,
      adminUser,
      required: true
    },
    nic: { name: nicName, valid: true, required: true },
    disk: { name: diskName, valid: true, required: true }
  };

  let allValid = kvResult.isValid && vmValid;

  if (useStorage && saResult) {
    resources.storage = {
      name: saResult.name,
      valid: saResult.isValid,
      length: saResult.length,
      maxLength: saResult.maxLength,
      errors: saResult.errors,
      purpose: 'Boot diagnostics',
      required: false
    };
    allValid = allValid && saResult.isValid;
  }

  if (useLaw && lawName) {
    const lawValid = lawName.length <= 63;
    resources.logAnalytics = {
      name: lawName,
      valid: lawValid,
      length: lawName.length,
      maxLength: 63,
      purpose: 'Monitoring and logging',
      required: false
    };
    allValid = allValid && lawValid;
  }

  return {
    resources,
    costs,
    options: { useStorageAccount: useStorage, useLogAnalytics: useLaw },
    allValid
  };
}

/**
 * Replaces POST /api/generate-package — generates all Bicep files + deploy script + README.
 * @param {object} state - Full wizard state from localStorage
 * @returns {{ files: Record<string, string> }}
 */
export function generatePackage(state) {
  const page2 = state.page2Discovery || {};
  const page3 = state.page3VMConfig || {};
  const page5 = state.page5StorageAccount || {};
  const page6 = state.page6LogAnalytics || {};
  const page7 = state.page7Tags || {};

  const subscription = page2.subscription || 'Default-Subscription';
  const resourceGroup = page2.resourceGroup || 'rg-default';
  const vnetName = page2.vnetName || 'vnet-default';
  const vnetRG = page2.vnetResourceGroup || 'rg-network';
  const subnetName = page2.subnetName || 'subnet-default';

  const { front9, back9 } = extractIdentity(subscription);

  // VM config
  const environment = page3.environment || 'production';
  const region = page3.region || 'eastus';
  const vmSize = page3.vmSize || 'Standard_B2s';
  const vmFunction = page3.vmFunction || '';
  const vmPurpose = page3.vmPurpose || '';
  const instance = page3.instance || '01';
  const adminUser = page3.adminUser || 'vmadmin';
  let finalVMName = page3.finalVMName || '';

  if (!finalVMName) {
    const envCode = environment.slice(0, 3);
    const regionCode = getRegionCode(region);
    finalVMName = `${envCode}${regionCode}${vmFunction}${vmPurpose}${instance}`;
  }

  const useStorage = page5.useStorageAccount || false;
  const useLaw = page6.useLogAnalytics || false;

  const kvResult = generateKeyvaultName(front9, back9, 'kv_infrastructure');
  const saResult = useStorage ? generateStorageAccountName(front9, back9, 'sa_boot_diagnostics') : null;
  const cleanedSub = subscription.replace(/[^a-zA-Z0-9]/g, '');
  const lawName = useLaw ? `law-${cleanedSub}` : null;

  // Tags
  const defaultTags = page7.defaultTags || {};
  const customTags = page7.customTags || [];
  const allTags = { ...defaultTags };
  for (const tag of customTags) {
    if (tag.key && tag.value) {
      allTags[tag.key] = tag.value;
    }
  }

  const timestamp = new Date().toISOString();
  const tagsBicep = buildTagsBicep(allTags);

  // KeyVault Bicep
  const keyvaultBicep = `// KeyVault Bicep Template
// Generated: ${timestamp}
// Subscription: ${subscription}

param kvName string = '${kvResult.name}'
param location string = '${region}'

resource kv 'Microsoft.KeyVault/vaults@2023-07-01' = {
  name: kvName
  location: location
  properties: {
    sku: { family: 'A', name: 'standard' }
    tenantId: subscription().tenantId
    enabledForDeployment: true
    enabledForTemplateDeployment: true
    enableRbacAuthorization: true
  }
  tags: {
    ${tagsBicep}
  }
}

output kvName string = kv.name
output kvId string = kv.id
`;

  // Storage Account Bicep (if selected)
  let storageBicep = '';
  if (useStorage && saResult) {
    storageBicep = `// Storage Account Bicep Template
// Generated: ${timestamp}

param saName string = '${saResult.name}'
param location string = '${region}'

resource storageAccount 'Microsoft.Storage/storageAccounts@2023-01-01' = {
  name: saName
  location: location
  tags: {
    ${tagsBicep}
  }
  sku: {
    name: 'Standard_LRS'
  }
  kind: 'StorageV2'
  properties: {
    accessTier: 'Hot'
    minimumTlsVersion: 'TLS1_2'
    supportsHttpsTrafficOnly: true
    allowBlobPublicAccess: false
  }
}

output storageAccountId string = storageAccount.id
output storageAccountName string = storageAccount.name
`;
  }

  // Log Analytics Bicep (if selected)
  let lawBicep = '';
  if (useLaw && lawName) {
    lawBicep = `// Log Analytics Workspace Bicep Template
// Generated: ${timestamp}

param lawName string = '${lawName}'
param location string = '${region}'
param retentionInDays int = 30

resource logAnalytics 'Microsoft.OperationalInsights/workspaces@2022-10-01' = {
  name: lawName
  location: location
  tags: {
    ${tagsBicep}
  }
  properties: {
    sku: {
      name: 'PerGB2018'
    }
    retentionInDays: retentionInDays
    publicNetworkAccessForIngestion: 'Enabled'
    publicNetworkAccessForQuery: 'Enabled'
  }
}

output workspaceId string = logAnalytics.id
output workspaceName string = logAnalytics.name
output customerId string = logAnalytics.properties.customerId
`;
  }

  // VM Bicep
  const vmBicep = `// VM Bicep Template
// Generated: ${timestamp}
// VM Name: ${finalVMName}

param vmName string = '${finalVMName}'
param adminUser string = '${adminUser}'
@secure()
param adminPwd string
param vmSize string = '${vmSize}'
param location string = '${region}'
param vnetName string = '${vnetName}'
param vnetRG string = '${vnetRG}'
param subnetName string = '${subnetName}'

resource vnet 'Microsoft.Network/virtualNetworks@2023-05-01' existing = {
  name: vnetName
  scope: resourceGroup(vnetRG)
}

resource subnet 'Microsoft.Network/virtualNetworks/subnets@2023-05-01' existing = {
  parent: vnet
  name: subnetName
}

resource nic 'Microsoft.Network/networkInterfaces@2023-05-01' = {
  name: 'nic-\${vmName}'
  location: location
  properties: {
    ipConfigurations: [{
      name: 'ipconfig1'
      properties: {
        subnet: { id: subnet.id }
        privateIPAllocationMethod: 'Dynamic'
      }
    }]
  }
  tags: {
    ${tagsBicep}
  }
}

resource vm 'Microsoft.Compute/virtualMachines@2023-09-01' = {
  name: vmName
  location: location
  properties: {
    hardwareProfile: { vmSize: vmSize }
    osProfile: {
      computerName: vmName
      adminUsername: adminUser
      adminPassword: adminPwd
      windowsConfiguration: {
        enableAutomaticUpdates: true
      }
    }
    storageProfile: {
      imageReference: {
        publisher: 'MicrosoftWindowsServer'
        offer: 'WindowsServer'
        sku: '2022-datacenter-azure-edition'
        version: 'latest'
      }
      osDisk: {
        name: 'disk-\${vmName}-os'
        createOption: 'FromImage'
        managedDisk: { storageAccountType: 'Standard_LRS' }
      }
    }
    networkProfile: {
      networkInterfaces: [{ id: nic.id }]
    }
    diagnosticsProfile: {
      bootDiagnostics: { enabled: true }
    }
  }
  tags: {
    ${tagsBicep}
  }
}

output vmName string = vm.name
output vmId string = vm.id
output privateIP string = nic.properties.ipConfigurations[0].properties.privateIPAddress
`;

  // PowerShell deployment script
  const deployCommands = [
    `az account set --subscription "${subscription}"`,
    `az deployment group create --resource-group ${resourceGroup} --template-file keyvault.bicep`
  ];

  if (useStorage) {
    deployCommands.push(`az deployment group create --resource-group ${resourceGroup} --template-file storage-account.bicep`);
  }
  if (useLaw) {
    deployCommands.push(`az deployment group create --resource-group ${resourceGroup} --template-file log-analytics.bicep`);
  }

  deployCommands.push(
    '$password = -join ((48..57) + (65..90) + (97..122) | Get-Random -Count 20 | ForEach-Object {[char]$_})',
    `az keyvault secret set --vault-name ${kvResult.name} --name vm-admin-password --value $password`,
    `az deployment group create --resource-group ${resourceGroup} --template-file vm.bicep --parameters vmName=${finalVMName} adminUser=${adminUser} adminPwd=$password`
  );

  const deployPs1 = `# Azure VM Deployment Script
# Generated: ${timestamp}
# VM Name: ${finalVMName}
# Created by: Azure Bicep Maker VM Wizard

${deployCommands.join('\n')}`;

  // README
  let resourceList = `- **KeyVault:** ${kvResult.name}`;
  if (useStorage && saResult) resourceList += `\n- **Storage Account:** ${saResult.name}`;
  if (useLaw && lawName) resourceList += `\n- **Log Analytics:** ${lawName}`;
  resourceList += `\n- **VM:** ${finalVMName}\n- **NIC:** nic-${finalVMName}\n- **Disk:** disk-${finalVMName}-os`;

  const readmeMd = `# Azure VM Deployment Package

**Generated:** ${timestamp}
**VM Name:** ${finalVMName}
**Subscription:** ${subscription}

## Resources

${resourceList}

## Quick Start

\`\`\`powershell
.\\deploy.ps1
\`\`\`

## Password Management

Retrieve password:
\`\`\`bash
az keyvault secret show --vault-name ${kvResult.name} --name vm-admin-password --query value -o tsv
\`\`\`

Rotate password:
\`\`\`bash
newPassword=$(openssl rand -base64 20)
az keyvault secret set --vault-name ${kvResult.name} --name vm-admin-password --value "$newPassword"
az vm user update --resource-group ${resourceGroup} --name ${finalVMName} --username ${adminUser} --password "$newPassword"
\`\`\`

---

Created by Dustin Winkler | Azure Bicep Maker VM Wizard
`;

  const files = {
    'keyvault.bicep': keyvaultBicep,
    'vm.bicep': vmBicep,
    'deploy.ps1': deployPs1,
    'README.md': readmeMd
  };

  if (useStorage) files['storage-account.bicep'] = storageBicep;
  if (useLaw) files['log-analytics.bicep'] = lawBicep;

  return { files };
}
